import { useMutation, useQuery } from '@tanstack/react-query';

import api from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';
import {
  isJobActive,
  type ReplyInbox,
  type ReplySendRequest,
  type ReplySendResult,
  type ReplySyncResult,
  type StatementConversation,
  type StatementLastSent,
  type StatementMailPreview,
  type StatementMailJob,
  type StatementMailJobRequest,
  type StatementReplySummary,
  type StatementRequest,
  type StatementResult,
  type StatementSendRequest,
  type StatementSendResult,
} from '../model/customerStatement.types';
import { customerStatementKeys } from '../queryKeys';

/** The payload out of either ApiResponse shape the backend uses (Data1 / data). */
const unwrap = <T,>(envelope: unknown): T | undefined => {
  const e = (envelope ?? {}) as Record<string, unknown>;
  return (e.Data1 ?? e.data1 ?? e.Data ?? e.data) as T | undefined;
};

const messageOf = (envelope: unknown, fallback: string): string => {
  const e = (envelope ?? {}) as Record<string, unknown>;
  return String(e.Message ?? e.message ?? fallback);
};

/**
 * The server's message when a call is refused, so the toast says why. The
 * axios interceptor rejects with an ApiError (body on `.data`, no
 * `.response`); a raw AxiosError is still read for callers outside it.
 */
const serverError = (error: unknown, fallback: string): Error => {
  const e = error as { data?: unknown; response?: { data?: unknown }; message?: string } | null;
  const data = e?.data ?? e?.response?.data;
  return new Error(data ? messageOf(data, e?.message || fallback) : (e?.message || fallback));
};

/**
 * Customer Statement API — the port of the legacy
 * `/CustomerReport/SelectCustomerStatementAllReport` call.
 *
 * Legacy answered `{ok: true}` and stashed the rows in the server session for
 * a Crystal viewer. This returns the statements themselves, so the screen can
 * show them and the overdue figures come from the server rather than a
 * browser-side loop that only ever kept the last customer's balance.
 */
export const customerStatementApi = {
  query: async (request: StatementRequest): Promise<StatementResult> => {
    const { data } = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.QUERY, request);
    const envelope = (data ?? {}) as Record<string, unknown>;
    const result = (envelope.Data1 ?? envelope.data1 ?? envelope.Data ?? envelope.data) as
      | StatementResult
      | undefined;
    if (!result) {
      throw new Error(String(envelope.Message ?? envelope.message ?? 'Statement could not be built'));
    }
    return result;
  },
};

/**
 * The API's absolute root, for a URL handed to a popup: the popup navigates
 * on its own, so the axios baseURL (often a bare path behind a proxy) has to
 * be resolved against the page first. Same helper the invoice print uses.
 */
const absoluteApiUrl = (path: string): string => {
  const base = api.defaults.baseURL ?? '';
  const root = base ? new URL(base, window.location.origin).href.replace(/\/+$/, '') : window.location.origin;
  return `${root}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Render the statement and get a URL the report window can open.
 *
 * Legacy's View opened ReportViewer.aspx in a popup that shared the session
 * cookie. A popup carries no bearer token, so the PDF is rendered by this
 * authenticated call and parked behind a random ticket that the URL names;
 * the popup collects it without a token, for a few minutes.
 */
export const printStatementUrl = async (request: StatementRequest): Promise<string> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.PRINT_TICKET, request);
  } catch (error) {
    throw serverError(error, 'The statement could not be prepared');
  }
  const envelope = (response.data ?? {}) as Record<string, unknown>;
  const payload = (envelope.Data1 ?? envelope.data1 ?? envelope.Data ?? envelope.data ?? {}) as Record<string, unknown>;
  const path = String(payload.Url ?? payload.url ?? '');
  if (!path) {
    throw new Error(String(envelope.Message ?? envelope.message ?? 'The statement could not be prepared'));
  }
  return absoluteApiUrl(path);
};

/**
 * The statement PDF as a blob: URL of this page, for an <iframe> preview.
 *
 * The ticket URL itself cannot be framed: the API is another origin
 * (localhost:8082 beside :5173, or the API host in production) and Spring
 * Security answers every response with `X-Frame-Options: DENY`, which the
 * browser shows as "refused to connect". Fetched here with the session and
 * handed over as a same-page blob it frames - the way the Receipt mail
 * dialog previews its voucher. The caller owns the URL and must revoke it.
 */
export const fetchStatementPdfObjectUrl = async (request: StatementRequest): Promise<string> => {
  const ticketUrl = await printStatementUrl(request);
  let response;
  try {
    response = await api.get(ticketUrl, { responseType: 'blob' });
  } catch (error) {
    throw serverError(error, 'The statement PDF could not be loaded');
  }
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
  return URL.createObjectURL(blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }));
};

/**
 * Build the statement as an Excel workbook and save it through the browser -
 * the report's Excel option, and the send window's "Download Excel" check.
 * Fetched with the session (a plain link would carry no token); the file name
 * is the server's, from Content-Disposition, else `fallbackName`.
 */
export const downloadStatementExcel = async (
  request: StatementRequest,
  fallbackName = 'CustomerStatement.xlsx'
): Promise<string> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.EXCEL, request, { responseType: 'blob' });
  } catch (error) {
    // with responseType 'blob' the error body is a Blob too; read the server's message out of it
    const data = (error as { data?: unknown })?.data;
    let body: unknown = null;
    if (data instanceof Blob) {
      try {
        body = JSON.parse(await data.text());
      } catch {
        body = null;
      }
    }
    throw body ? new Error(messageOf(body, 'The Excel statement could not be prepared'))
      : serverError(error, 'The Excel statement could not be prepared');
  }
  const disposition = String(
    (response.headers as Record<string, unknown> | undefined)?.['content-disposition'] ?? ''
  );
  const fileName = /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? fallbackName;
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // give the browser a moment to start the save before the URL goes away
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return fileName;
};

/**
 * An explicit action (the View button), so a mutation rather than a query:
 * nothing should refetch on focus and re-run a 156-customer statement.
 */
export function useBuildCustomerStatement(options: {
  onSuccess?: (result: StatementResult) => void;
  onError?: (error: unknown) => void;
} = {}) {
  return useMutation({
    mutationFn: (request: StatementRequest) => customerStatementApi.query(request),
    ...options,
  });
}

/**
 * Send one customer's statement, PDF attached. The server rebuilds the
 * statement from the same filters, so what is quoted in the mail is what is
 * attached. Throws with the server's message on refusal - a mail that did
 * not go out must not look like one that did.
 */
export const sendStatement = async (request: StatementSendRequest): Promise<StatementSendResult> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.SEND, request);
  } catch (error) {
    throw serverError(error, 'The statement could not be sent');
  }
  const envelope = (response.data ?? {}) as Record<string, unknown>;
  const result = (envelope.Data1 ?? envelope.data1 ?? envelope.Data ?? envelope.data) as StatementSendResult | undefined;
  if (!result) {
    throw new Error(String(envelope.Message ?? envelope.message ?? 'The statement could not be sent'));
  }
  return result;
};

/**
 * The mail {@link sendStatement} would send for these filters and wording -
 * subject, filled HTML body, To and CC - without rendering or sending. Left
 * `emails` out (null) to get the customer's own statement addresses.
 */
export const fetchStatementMailPreview = async (
  request: StatementRequest & { reminder: string; emails: string | null }
): Promise<StatementMailPreview> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.MAIL_PREVIEW, request);
  } catch (error) {
    throw serverError(error, 'The statement mail could not be prepared');
  }
  const preview = unwrap<StatementMailPreview>(response.data);
  if (!preview) throw new Error(messageOf(response.data, 'The statement mail could not be prepared'));
  return preview;
};

// ── Send Statements workspace (bulk run) ─────────────────────────────────────

/**
 * Start a run. The server builds every statement in one pass, answers at
 * once with the run, and mails in the background; follow it with
 * {@link useStatementMailJob}. Refused (a run already going, no customer,
 * no mail server) with the server's reason.
 */
export const startStatementMailJob = async (request: StatementMailJobRequest): Promise<StatementMailJob> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.MAIL_JOBS, request);
  } catch (error) {
    throw serverError(error, 'The run could not be started');
  }
  const job = unwrap<StatementMailJob>(response.data);
  if (!job) throw new Error(messageOf(response.data, 'The run could not be started'));
  return job;
};

export const fetchStatementMailJob = async (id: string): Promise<StatementMailJob> => {
  let response;
  try {
    response = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.MAIL_JOB(id));
  } catch (error) {
    throw serverError(error, 'This run is no longer available');
  }
  const job = unwrap<StatementMailJob>(response.data);
  if (!job) throw new Error(messageOf(response.data, 'This run is no longer available'));
  return job;
};

/** The run in progress for the company, or null — so a reloaded screen picks it up again. */
export const fetchActiveStatementMailJob = async (companyId: number): Promise<StatementMailJob | null> => {
  const { data } = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.MAIL_JOBS_ACTIVE, { params: { companyId } });
  return unwrap<StatementMailJob>(data) ?? null;
};

export const cancelStatementMailJob = async (id: string): Promise<StatementMailJob> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.MAIL_JOB_CANCEL(id));
  } catch (error) {
    throw serverError(error, 'The run could not be cancelled');
  }
  const job = unwrap<StatementMailJob>(response.data);
  if (!job) throw new Error(messageOf(response.data, 'The run could not be cancelled'));
  return job;
};

export const fetchLastSent = async (companyId: number): Promise<StatementLastSent[]> => {
  const { data } = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.LAST_SENT, { params: { companyId } });
  return unwrap<StatementLastSent[]>(data) ?? [];
};

/** Polls once a second while the run is going, then stops on its own. */
export function useStatementMailJob(id: string | null) {
  return useQuery({
    queryKey: customerStatementKeys.mailJob(id ?? ''),
    queryFn: () => fetchStatementMailJob(id as string),
    enabled: !!id,
    refetchInterval: (query) => (isJobActive(query.state.data) ? 1000 : false),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useActiveStatementMailJob(companyId: number | null | undefined) {
  return useQuery({
    queryKey: customerStatementKeys.activeMailJob(companyId ?? 0),
    queryFn: () => fetchActiveStatementMailJob(companyId as number),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useLastSent(companyId: number | null | undefined) {
  return useQuery({
    queryKey: customerStatementKeys.lastSent(companyId ?? 0),
    queryFn: () => fetchLastSent(companyId as number),
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

// ── Replies from customers, and answering them ──────────────────────────────

export const fetchLatestReplies = async (companyId: number): Promise<StatementReplySummary[]> => {
  const { data } = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLIES_LATEST, { params: { companyId } });
  return unwrap<StatementReplySummary[]>(data) ?? [];
};

export const fetchConversation = async (companyId: number, customerId: number): Promise<StatementConversation> => {
  const { data } = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLIES_CONVERSATION, { params: { companyId, customerId } });
  const conversation = unwrap<StatementConversation>(data);
  if (!conversation) throw new Error(messageOf(data, 'The conversation could not be loaded'));
  return conversation;
};

export const markReplyRead = async (id: number, companyId: number): Promise<void> => {
  await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLY_READ(id), null, { params: { companyId } });
};

/** Reply, Reply All, or a fresh mail to the customer — from the accounts address, threaded and logged. */
export const sendStatementReply = async (request: ReplySendRequest): Promise<ReplySendResult> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLIES_SEND, request);
  } catch (error) {
    throw serverError(error, 'The reply could not be sent');
  }
  const result = unwrap<ReplySendResult>(response.data);
  if (!result) throw new Error(messageOf(response.data, 'The reply could not be sent'));
  return result;
};

/** Read the mailbox now instead of waiting for the timer. */
export const syncReplies = async (): Promise<ReplySyncResult> => {
  let response;
  try {
    response = await api.post(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLIES_SYNC);
  } catch (error) {
    throw serverError(error, 'The mailbox could not be read');
  }
  const result = unwrap<ReplySyncResult>(response.data);
  if (!result) throw new Error(messageOf(response.data, 'The mailbox could not be read'));
  return result;
};

/**
 * Open a reply's attachment. The file sits behind the JWT, so it is fetched
 * with the token and handed to the browser as a blob URL rather than linked.
 */
export const openReplyAttachment = async (replyId: number, attachmentId: number, companyId: number, fileName: string) => {
  const { data } = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLY_ATTACHMENT(replyId, attachmentId), {
    params: { companyId },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** The header bell's list: unread replies for the company. The server answers `allowed: false` for roles not on the notify list. */
export const fetchReplyInbox = async (companyId: number, limit = 15): Promise<ReplyInbox> => {
  const { data } = await api.get(API_ENDPOINTS.CUSTOMER_STATEMENT.REPLIES_UNREAD, { params: { companyId, limit } });
  return unwrap<ReplyInbox>(data) ?? { allowed: false, count: 0, items: [] };
};

/**
 * Polled every minute as a fallback; the WebSocket signal from the mailbox
 * reader invalidates it the moment new replies are stored.
 */
export function useReplyInbox(companyId: number | null | undefined) {
  return useQuery({
    queryKey: customerStatementKeys.inbox(companyId ?? 0),
    queryFn: () => fetchReplyInbox(companyId as number),
    enabled: !!companyId,
    refetchInterval: 60_000,
    staleTime: 20_000,
    retry: false,
  });
}

/** Who has replied, refreshed every minute while the screen is open. */
export function useLatestReplies(companyId: number | null | undefined) {
  return useQuery({
    queryKey: customerStatementKeys.replies(companyId ?? 0),
    queryFn: () => fetchLatestReplies(companyId as number),
    enabled: !!companyId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useConversation(companyId: number | null | undefined, customerId: number | null) {
  return useQuery({
    queryKey: customerStatementKeys.conversation(companyId ?? 0, customerId ?? 0),
    queryFn: () => fetchConversation(companyId as number, customerId as number),
    enabled: !!companyId && !!customerId,
    refetchOnWindowFocus: false,
  });
}
